import { t } from '../i18n.js';
import { pendingBadgeFor } from '../pages/outbox-pending.js';
import { STATUS_PILL_CLASS } from './status-pill.js';

// ---------------------------------------------------------------------------
// Status pill — the `default` branch is a defensive fallback only:
// pages/timers.js only ever renders timers returned by `listActiveByHousehold`
// (running|paused|elapsed; cancelled rows are soft-retained and excluded), so
// this branch should never actually render.
// ---------------------------------------------------------------------------

function statusPillProps(status) {
  switch (status) {
    case 'running':
      return { label: t('timers.status_running'), className: STATUS_PILL_CLASS.success };
    case 'paused':
      return { label: t('timers.status_paused'), className: STATUS_PILL_CLASS.warning };
    case 'elapsed':
      return { label: t('timers.status_elapsed'), className: STATUS_PILL_CLASS.danger };
    default:
      return { label: '—', className: 'chip bg-border dark:bg-surface-card-dark text-ink-muted' };
  }
}

/**
 * Renders one timer card: status pill, countdown display, and the controls
 * relevant to the timer's current status (pause/resume + add-minute while
 * running/paused; restart + dismiss while elapsed; cancel otherwise).
 *
 * The countdown text is seeded from `displayText`, which the caller computes
 * via the `formatDuration`/`deriveDisplayMs` pure helpers exported from
 * pages/timers.js — this component never duplicates countdown math. The
 * returned `remainingEl` is the caller's hook for the 1 Hz tick to keep
 * updating the display between polls.
 *
 * @param {{id: string, label: string|null, status: string, symbol?: string|null,
 *   _pending?: boolean, _outboxKey?: string}} timer
 * @param {{onControl: (id: string, action: string) => void,
 *   onCancel: (id: string, intent: 'cancel'|'dismiss') => void}} handlers
 * @param {string} displayText
 * @returns {{el: HTMLElement, remainingEl: HTMLElement}}
 */
export function timerCard(timer, handlers, displayText) {
  const el = document.createElement('div');
  el.className = 'card space-y-2 hover-lift';
  el.dataset.role = 'timer-card';
  el.dataset.timerId = timer.id;

  const topRow = document.createElement('div');
  topRow.className = 'flex items-start justify-between gap-2';

  const nameEl = document.createElement('span');
  nameEl.className = 'font-semibold text-base truncate';
  // textContent only — `symbol`/`label` are user-supplied and must never be
  // parsed as markup.
  const label = timer.label || t('timers.unnamed_label');
  nameEl.textContent = timer.symbol ? `${timer.symbol} ${label}` : label;
  topRow.append(nameEl);

  const pill = statusPillProps(timer.status);
  const pillEl = document.createElement('span');
  pillEl.className = `${pill.className} text-xs shrink-0`;
  pillEl.dataset.role = 'timer-status-pill';
  pillEl.textContent = pill.label;
  topRow.append(pillEl);

  // Shared offline-pending indication (IG-R10) — the same descriptor the
  // inventory list uses, so the badge reads identically wherever a mutation is
  // waiting for the service-worker outbox to replay it.
  if (timer._pending) {
    const bd = pendingBadgeFor(timer._outboxKey || '');
    const pendingBadge = document.createElement('span');
    pendingBadge.className = `${bd.className} shrink-0`;
    pendingBadge.dataset.role = bd.dataRole;
    pendingBadge.textContent = t(bd.i18nKey);
    topRow.append(pendingBadge);
  }

  el.append(topRow);

  const remainingEl = document.createElement('div');
  remainingEl.className = 'text-2xl font-semibold tabular-nums';
  remainingEl.dataset.role = 'timer-remaining';
  remainingEl.textContent = displayText;
  el.append(remainingEl);

  const controlsRow = document.createElement('div');
  controlsRow.className = 'flex flex-wrap gap-2';

  if (timer.status === 'running' || timer.status === 'paused') {
    const toggleBtn = document.createElement('button');
    toggleBtn.type = 'button';
    toggleBtn.className = 'btn text-sm';
    toggleBtn.dataset.role = timer.status === 'running' ? 'timer-pause' : 'timer-resume';
    toggleBtn.textContent = timer.status === 'running' ? t('timers.pause') : t('timers.resume');
    toggleBtn.addEventListener('click', () => handlers.onControl(timer.id, timer.status === 'running' ? 'pause' : 'resume'));
    controlsRow.append(toggleBtn);

    const addMinuteBtn = document.createElement('button');
    addMinuteBtn.type = 'button';
    addMinuteBtn.className = 'btn text-sm';
    addMinuteBtn.dataset.role = 'timer-add-minute';
    addMinuteBtn.textContent = t('timers.add_minute');
    addMinuteBtn.addEventListener('click', () => handlers.onControl(timer.id, 'add_minute'));
    controlsRow.append(addMinuteBtn);
  }

  if (timer.status === 'elapsed') {
    const restartBtn = document.createElement('button');
    restartBtn.type = 'button';
    restartBtn.className = 'btn text-sm';
    restartBtn.dataset.role = 'timer-restart';
    restartBtn.textContent = t('timers.restart');
    restartBtn.addEventListener('click', () => handlers.onControl(timer.id, 'restart'));
    controlsRow.append(restartBtn);

    const dismissBtn = document.createElement('button');
    dismissBtn.type = 'button';
    dismissBtn.className = 'btn text-sm text-danger';
    dismissBtn.dataset.role = 'timer-dismiss';
    dismissBtn.textContent = t('timers.dismiss');
    // The intent is passed through so the confirmation reads back the button
    // the user actually pressed ("Timer dismissed." vs "Timer cancelled.").
    dismissBtn.addEventListener('click', () => handlers.onCancel(timer.id, 'dismiss'));
    controlsRow.append(dismissBtn);
  } else {
    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'btn text-sm text-danger';
    cancelBtn.dataset.role = 'timer-cancel';
    cancelBtn.textContent = t('timers.cancel');
    cancelBtn.addEventListener('click', () => handlers.onCancel(timer.id, 'cancel'));
    controlsRow.append(cancelBtn);
  }

  el.append(controlsRow);

  return { el, remainingEl };
}
